<template>
  <Navbar />
  <div class="body otp-container auth-page-skin" :dir="rtl ? 'rtl' : 'ltr'">

    <div class="otp-card auth-panel">
      <h2>{{ $t('otp.title') }}</h2>

      <p class="subtitle">
        {{ $t('otp.subtitle') }} {{ maskedPhone }}
      </p>

      <div class="otp-input-group" style="direction:ltr;">
        <input
          v-for="(digit,index) in otpDigits"
          :key="index"
          v-model="otpDigits[index]"
          type="text"
          maxlength="1"
          class="otp-input"
          :ref="el => otpInputs[index] = el"
          @input="handleOtpInput(index,$event)"
          @keydown="handleOtpKeydown(index,$event)"
          @paste="handleOtpPaste"
        />
      </div>

      <div class="timer" v-if="timer > 0">
        {{ $t('otp.resendIn') }} {{ formattedTime }}
      </div>

      <button
        class="submit-btn"
        :disabled="!isOtpComplete || loading"
        @click="verifyAndContinue"
      >
        {{ loading ? $t('otp.verifying') : $t('otp.verifyOtp') }}
      </button>

      <button
        class="resend-btn"
        :disabled="timer > 0 || resendLoading"
        @click="handleResendOtp"
      >
        {{ resendLoading ? $t('otp.sending') : $t('otp.resendOtp') }}
      </button>

      <div class="back-link">
        <router-link to="/reset-password">
          {{ $t('otp.back') }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import Navbar from '@/components/navbar_styled_register.vue';

export default {
  name: 'ResetPasswordOtp',
  components: { Navbar },
  setup() {
    const router = useRouter()
    const otpDigits = ref(['', '', '', '', '', ''])
    const otpInputs = ref([])
    const loading = ref(false)
    const resendLoading = ref(false)
    const timer = ref(120) // 2 minutes
    const phoneNumber = ref('')
    const currentLang = ref('fa')

    const translations = {
      en: {
        title: "Verify OTP",
        subtitle: "Enter the 6-digit code sent to",
        verifyOtp: "Verify & Continue",
        verifying: "Verifying...",
        resendOtp: "Resend OTP",
        sending: "Sending...",
        resendIn: "Resend code in",
        back: "Back",
        otpComplete: "OTP verification successful",
        otpIncomplete: "Please enter all 6 digits"
      },
      fa: {
        title: "تأیید کد",
        subtitle: "کد ۶ رقمی ارسال شده به",
        verifyOtp: "تأیید و ادامه",
        verifying: "در حال تأیید...",
        resendOtp: "ارسال مجدد کد",
        sending: "در حال ارسال...",
        resendIn: "ارسال مجدد کد در",
        back: "بازگشت",
        otpComplete: "تأیید کد موفقیت‌آمیز بود",
        otpIncomplete: "لطفاً هر ۶ رقم را وارد کنید"
      },
      ar: {
        title: "التحقق من رمز التحقق",
        subtitle: "أدخل الرمز المكون من 6 أرقام المرسل إلى",
        verifyOtp: "تحقق والمتابعة",
        verifying: "جاري التحقق...",
        resendOtp: "إعادة إرسال رمز التحقق",
        sending: "جاري الإرسال...",
        resendIn: "إعادة الإرسال في",
        back: "رجوع",
        otpComplete: "تم التحقق من رمز التحقق بنجاح",
        otpIncomplete: "الرجاء إدخال جميع الأرقام الستة"
      }
    }

    const direction = computed(() => {
      return currentLang.value === 'fa' || currentLang.value === 'ar' ? 'rtl' : 'ltr'
    })

    const maskedPhone = computed(() => {
      if (!phoneNumber.value) return ''
      const phone = phoneNumber.value
      return `${phone.slice(-4)}***${phone.slice(0, 4)}`
    })

    const otpCode = computed(() => {
      return otpDigits.value.join('')
    })

    const isOtpComplete = computed(() => {
      return otpCode.value.length === 6 && otpDigits.value.every(digit => digit !== '')
    })

    const formattedTime = computed(() => {
      const minutes = Math.floor(timer.value / 60)
      const seconds = timer.value % 60
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    })

    const handleOtpInput = (index, event) => {
      const value = event.target.value
      
      // Only allow numbers
      if (!/^\d*$/.test(value)) {
        otpDigits.value[index] = ''
        return
      }

      otpDigits.value[index] = value

      // Auto-focus next input if a digit was entered
      if (value && index < 5) {
        otpInputs.value[index + 1]?.focus()
      }

      // If last digit was entered, blur the input
      if (value && index === 5) {
        otpInputs.value[index]?.blur()
      }
    }

    const handleOtpKeydown = (index, event) => {
      // Handle backspace
      if (event.key === 'Backspace') {
        if (!otpDigits.value[index] && index > 0) {
          otpDigits.value[index - 1] = ''
          otpInputs.value[index - 1]?.focus()
        }
      }
      
      // Handle arrow keys
      if (event.key === 'ArrowLeft' && index > 0) {
        otpInputs.value[index - 1]?.focus()
      }
      if (event.key === 'ArrowRight' && index < 5) {
        otpInputs.value[index + 1]?.focus()
      }
    }

    const handleOtpPaste = (event) => {
      event.preventDefault()
      const pasteData = event.clipboardData.getData('text').trim()
      
      if (/^\d{6}$/.test(pasteData)) {
        const digits = pasteData.split('')
        digits.forEach((digit, index) => {
          if (index < 6) {
            otpDigits.value[index] = digit
          }
        })
        
        // Focus the last input after pasting
        if (otpInputs.value[5]) {
          otpInputs.value[5].focus()
        }
      }
    }

    const verifyAndContinue = () => {
      if (!isOtpComplete.value) {
        alert(translations[currentLang.value].otpIncomplete)
        return
      }

      // Save OTP to localStorage for the next step
      localStorage.setItem('resetOtp', otpCode.value)
      
      // Navigate to set new password view
      router.push('/reset-new-password')
    }

    const handleResendOtp = async () => {
      resendLoading.value = true
      
      try {
        // Here you would call the API to resend OTP
        // For now, we'll just reset the timer
        timer.value = 120
        alert(translations[currentLang.value].otpComplete)
      } catch (error) {
        alert('Failed to resend OTP. Please try again.')
      } finally {
        resendLoading.value = false
      }
    }

    const switchLanguage = () => {
      localStorage.setItem('selectedLanguage', currentLang.value)
    }

    const startTimer = () => {
      const interval = setInterval(() => {
        if (timer.value > 0) {
          timer.value--
        }
      }, 1000)
      
      return interval
    }

    onMounted(() => {
      // Get phone number from localStorage
      const savedPhone = localStorage.getItem('resetPhoneNumber')
      if (!savedPhone) {
        router.push('/reset-password')
        return
      }
      phoneNumber.value = savedPhone

      // Get saved language
      const savedLang = localStorage.getItem('selectedLanguage') || 'fa'
      currentLang.value = savedLang

      // Focus first input
      if (otpInputs.value[0]) {
        otpInputs.value[0].focus()
      }

      // Start timer
      const timerInterval = startTimer()
      
      // Cleanup on unmount
      onUnmounted(() => {
        clearInterval(timerInterval)
      })
    })

    return {
      otpDigits,
      otpInputs,
      loading,
      resendLoading,
      timer,
      phoneNumber,
      currentLang,
      translations,
      direction,
      maskedPhone,
      otpCode,
      isOtpComplete,
      formattedTime,
      handleOtpInput,
      handleOtpKeydown,
      handleOtpPaste,
      verifyAndContinue,
      handleResendOtp,
      switchLanguage
    }
  }
}
</script>

<style scoped>
.otp-container{
  display:flex;
  justify-content:center;
  align-items:center;
  min-height:100vh;
  background:var(--bg);
  padding:1rem;
}

.otp-card{
  background:var(--card-bg);
  color:var(--text);
  padding:2rem;
  border-radius:12px;
  border:1px solid var(--border);
  box-shadow:var(--shadow);
  width:100%;
  max-width:420px;
  text-align:center;
}

.otp-card h2{
  color:var(--primary);
  margin-bottom:0.5rem;
}

.subtitle{
  color:var(--text-secondary);
  margin-bottom:1.5rem;
}

.otp-input-group{
  display:flex;
  justify-content:center;
  gap:0.5rem;
  margin-bottom:1.5rem;
}

.otp-input{
  width:45px;
  height:60px;
  text-align:center;
  font-size:1.5rem;
  border:1px solid var(--border);
  border-radius:6px;
  background:var(--input-bg);
  color:var(--text);
}

.otp-input:focus{
  border-color:var(--primary);
  outline:none;
  box-shadow:0 0 0 3px var(--focus-ring);
}

.timer{
  color:var(--text-secondary);
  margin-bottom:1rem;
}

.submit-btn{
  width:100%;
  background:var(--primary);
  color:white;
  border:none;
  padding:0.75rem;
  border-radius:6px;
  cursor:pointer;
  margin-bottom:1rem;
}

.submit-btn:disabled{
  background:var(--disabled);
}

.resend-btn{
  width:100%;
  background:var(--secondary-bg);
  color:var(--text);
  border:1px solid var(--border);
  padding:0.75rem;
  border-radius:6px;
  cursor:pointer;
  margin-bottom:1rem;
}

.resend-btn:disabled{
  opacity:0.6;
}

.back-link{
  margin-top:1rem;
}

.back-link a{
  color:var(--primary);
}


/* Shared Register-style auth UI */
.auth-page-skin { --auth-text:#f1f7f5; --auth-muted:#9bb5b4; --auth-surface:rgba(12,42,51,.72); --auth-border:rgba(145,205,194,.2); --auth-input:rgba(5,24,31,.72); --auth-accent:#f07832; --auth-accent-soft:#ff9b5f; position:relative; isolation:isolate; min-height:calc(100svh - 64px); overflow:hidden; padding:56px 20px 72px; color:var(--auth-text); background:radial-gradient(circle at 72% 45%,rgba(35,110,118,.25),transparent 34%),linear-gradient(135deg,#06151e 0%,#08242e 52%,#071b25 100%); font-family:'Vazirmatn',Tahoma,'Segoe UI',sans-serif; }
.auth-page-skin::before { content:''; position:absolute; z-index:-1; inset:-12%; background:url('~@/assets/register-login-bg.svg') center / min(720px,92vw) auto no-repeat; opacity:.34; pointer-events:none; }
.auth-page-skin::after { content:''; position:absolute; z-index:-1; inset:20%; border-radius:50%; background:rgba(240,120,50,.12); filter:blur(70px); pointer-events:none; }
.auth-page-skin > * { position:relative; z-index:1; }
.auth-page-skin .auth-panel { position:relative; z-index:2; width:min(100%,440px); margin:0 auto; padding:36px 32px 30px; border:1px solid var(--auth-border); border-radius:22px; background:var(--auth-surface); color:var(--auth-text); box-shadow:0 24px 70px rgba(0,0,0,.28),inset 0 1px 0 rgba(255,255,255,.04); backdrop-filter:blur(14px); }
.auth-page-skin .auth-panel h2 { color:var(--auth-text); font-size:clamp(22px,3vw,29px); font-weight:800; }
.auth-page-skin .subtitle { color:var(--auth-muted); line-height:1.8; }
.auth-page-skin .form-group { margin-bottom:18px; }
.auth-page-skin .form-label { color:var(--auth-muted); font-size:13px; font-weight:600; }
.auth-page-skin .form-input, .auth-page-skin .otp-input { width:100%; min-height:46px; padding:11px 14px; border:1px solid rgba(145,205,194,.25); border-radius:10px; outline:none; background:var(--auth-input); color:var(--auth-text); font:inherit; transition:border-color .2s,box-shadow .2s,background .2s; }
.auth-page-skin .form-input::placeholder { color:#76918f; }
.auth-page-skin .form-input:focus, .auth-page-skin .otp-input:focus { border-color:var(--auth-accent); box-shadow:0 0 0 3px rgba(240,120,50,.16); background:rgba(5,24,31,.88); }
.auth-page-skin .form-input.input-error { border-color:#e07070; }
.auth-page-skin .btn-primary, .auth-page-skin .verify-btn, .auth-page-skin .login-btn, .auth-page-skin .reset-btn, .auth-page-skin .password-btn, .auth-page-skin .submit-btn { min-height:46px; border:0; border-radius:10px; background:var(--auth-accent); color:#fff; font:inherit; font-weight:700; cursor:pointer; box-shadow:0 8px 24px rgba(240,120,50,.2); transition:transform .18s,background .18s,opacity .18s,box-shadow .18s; }
.auth-page-skin .btn-primary:hover:not(:disabled), .auth-page-skin .verify-btn:hover:not(:disabled), .auth-page-skin .login-btn:hover:not(:disabled), .auth-page-skin .reset-btn:hover:not(:disabled), .auth-page-skin .password-btn:hover:not(:disabled), .auth-page-skin .submit-btn:hover:not(:disabled) { transform:translateY(-2px); background:var(--auth-accent-soft); box-shadow:0 12px 32px rgba(240,120,50,.3); }
.auth-page-skin button:disabled { opacity:.65; cursor:wait; }
.auth-page-skin .text-danger, .auth-page-skin .error-message { color:#f28a8a; }
.auth-page-skin .text-success, .auth-page-skin .success-message { color:#7ed6a2; }
.auth-page-skin .error-message, .auth-page-skin .success-message { background:rgba(5,24,31,.48); border-color:var(--auth-border); }
.auth-page-skin .btn-secondary { background:#31545b; color:#fff; border:0; }
.auth-page-skin .btn-link, .auth-page-skin a { color:var(--auth-accent-soft); }
.auth-page-skin .role-selection-content, .auth-page-skin .company-selection-content { background:#0d2b35; color:var(--auth-text); border:1px solid var(--auth-border); }
.auth-page-skin .company-option { border-color:var(--auth-border); background:rgba(5,24,31,.38); }
@media (max-width:767px) { .auth-page-skin { min-height:calc(100svh - 56px); padding:34px 14px 40px; } .auth-page-skin::before { background-size:min(560px,125vw) auto; opacity:.28; } .auth-page-skin .auth-panel { width:min(100%,440px); padding:28px 22px; } }


/* Match Register composition: visual left, auth panel right; visual behind panel on mobile. */
.otp-container.auth-page-skin, .verify-container.auth-page-skin, .password-container.auth-page-skin, .reset-container.auth-page-skin {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr);
  align-items: center;
  gap: clamp(24px, 4vw, 72px);
  width: 100%;
  min-height: calc(100svh - 64px);
  padding: 72px clamp(24px, 5vw, 88px) 32px;
}
.otp-container.auth-page-skin::before, .verify-container.auth-page-skin::before, .password-container.auth-page-skin::before, .reset-container.auth-page-skin::before {
  inset: 8% 42% 4% 2%;
  background-size: min(680px, 56vw) auto;
  background-position: center;
  opacity: .62;
}
.otp-container.auth-page-skin > .auth-panel, .verify-container.auth-page-skin > .auth-panel, .password-container.auth-page-skin > .auth-panel, .reset-container.auth-page-skin > .auth-panel {
  grid-column: 2;
  justify-self: end;
  width: min(100%, 500px);
}
.reset-container.auth-page-skin > .language-switcher { position: absolute; z-index: 3; top: 18px; inset-inline-start: 24px; }
@media (max-width: 767px) {
  .otp-container.auth-page-skin, .verify-container.auth-page-skin, .password-container.auth-page-skin, .reset-container.auth-page-skin {
    position: relative;
    display: block;
    min-height: calc(100svh - 56px);
    padding: 34px 14px 40px;
  }
  .otp-container.auth-page-skin::before, .verify-container.auth-page-skin::before, .password-container.auth-page-skin::before, .reset-container.auth-page-skin::before {
    inset: -8% -18%;
    background-size: min(560px, 125vw) auto;
    opacity: .34;
  }
  .otp-container.auth-page-skin > .auth-panel, .verify-container.auth-page-skin > .auth-panel, .password-container.auth-page-skin > .auth-panel, .reset-container.auth-page-skin > .auth-panel {
    position: relative;
    z-index: 2;
    width: min(100%, 440px);
    margin: 0 auto;
  }
}

/* RegisterCompanyPhone has an extra inner container around its panel. */
.auth-page-skin > .login-container {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr);
  align-items: center;
  gap: clamp(24px, 4vw, 72px);
  min-height: calc(100svh - 64px);
  padding: 0;
  background: transparent;
}
.auth-page-skin > .login-container > .login-card {
  grid-column: 2;
  justify-self: end;
  width: min(100%, 500px);
  position: relative;
  z-index: 2;
  background: var(--auth-surface);
  border: 1px solid var(--auth-border);
  border-radius: 22px;
  color: var(--auth-text);
  box-shadow: 0 24px 70px rgba(0,0,0,.28);
  backdrop-filter: blur(14px);
}
@media (max-width: 767px) {
  .auth-page-skin > .login-container { display: block; min-height: calc(100svh - 56px); }
  .auth-page-skin > .login-container > .login-card { width: min(100%, 440px); margin: 0 auto; }
}


/* Keep auth panels translucent like Register so the visual fades through them. */
.auth-page-skin .auth-panel,
.auth-page-skin .login-card,
.register-page .form-card {
  background: transparent !important;
  backdrop-filter: blur(4px);
}

/* Center the visual directly behind the panel on phone widths. */
@media (max-width: 767px) {
  .auth-page-skin::before {
    inset: 0;
    background-position: center center;
    background-size: min(112vw, 440px) auto;
    opacity: .5;
  }

  .auth-page-skin .auth-panel,
  .auth-page-skin .login-card,
  .register-page .form-card {
    position: relative;
    z-index: 4;
    background: transparent !important;
    margin-top: 150px !important;
    box-shadow: 0 18px 44px rgba(0, 0, 0, .18);
  }
}

/* Login fields must stay dark, including browser autofill. */
.register-page .form-card .form-input,
.login-page .form-card .form-input,
.login-page input.form-input {
  background: #05181f !important;
  background-color: #05181f !important;
  color: #f1f7f5 !important;
}

.register-page .form-card .form-input:-webkit-autofill,
.login-page .form-card .form-input:-webkit-autofill,
.login-page input.form-input:-webkit-autofill,
.login-page .form-input:-webkit-autofill:hover,
.login-page .form-input:-webkit-autofill:focus {
  -webkit-text-fill-color: #f1f7f5 !important;
  -webkit-box-shadow: 0 0 0 1000px #05181f inset !important;
  box-shadow: 0 0 0 1000px #05181f inset !important;
  background-color: #05181f !important;
  caret-color: #f1f7f5;
}
.otp-container.auth-page-skin,
.verify-container.auth-page-skin,
.password-container.auth-page-skin,
.reset-container.auth-page-skin,
.login-container.auth-page-skin {
  height: auto !important;
  min-height: 100dvh !important;
}

</style>
